import { useCallback, useEffect, useRef, useState } from 'react'

export interface AsyncState<T> {
  data: T | undefined
  error: string | undefined
  loading: boolean
  reload: () => void
}

/** Run an async function and track its state.
 *
 *  Results are discarded if a newer call has started, so changing the station
 *  twice quickly cannot leave the first station's data on screen under the
 *  second station's heading. A series query over a year takes long enough for
 *  that to be reachable by hand.
 *
 *  **`data` is cleared the moment a new call begins.** Keeping the previous
 *  result until the next one arrives is the usual way to avoid a flicker, and
 *  it is wrong here: the old chart stays drawn under the new station's name,
 *  and a small "loading" line above it is easy to miss. A reader who glances
 *  at a year of Lajanuri while the heading says Shaori has no way to know.
 *  Better a gap that is obviously a gap.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T>()
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const generation = useRef(0)

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    const mine = ++generation.current
    setLoading(true)
    setError(undefined)
    setData(undefined)
    fn().then(
      (result) => {
        if (mine !== generation.current) return
        setData(result)
        setLoading(false)
      },
      (cause: unknown) => {
        if (mine !== generation.current) return
        setError(cause instanceof Error ? cause.message : String(cause))
        setLoading(false)
      },
    )
    // fn is rebuilt every render by callers; deps is the real dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return { data, error, loading, reload }
}

/** An answer that a refresh clears, held on screen until the next one: useAsync
 *  clears at every call, right for a new question and wrong for the same one asked
 *  again. A new `question` lets go of what was held. */
export function useHeld<T>(value: T | undefined, question: unknown): T | undefined {
  const [held, setHeld] = useState<{ question: unknown; value: T }>()
  useEffect(() => { if (value !== undefined) setHeld({ question, value }) }, [value, question])
  return value ?? (held && held.question === question ? held.value : undefined)
}
